import { supabase } from "@/integrations/supabase/client";
import type {
  BookingPaymentFlow,
  BookingDetail,
  BookingSchedulePolicy,
  BookingScheduleResponse,
  BookingSlotPreview,
  ConflictAuditEvent,
  CreateBookingV2Response,
  PlayerBookingListItem,
} from "@/types/services/api-contracts";

import { callEdgeData } from "./_core/edge-call";
import { BOOKING_FUNCTION_NAME } from "./_core/function-names";

export interface TenantBooking {
  id: string;
  tenant_id: string;
  venue_id: string;
  court_id: string;
  created_by?: string | null;
  start_at: string;
  end_at: string;
  status: string;
  payment_status: string;
  total_amount_cents: number;
  notes?: string | null;
  booking_source?: string | null;
  created_at: string;
  venues?: { name?: string | null } | null;
  courts?: { name?: string | null } | null;
  profiles?: { full_name?: string | null } | null;
}

export const bookingService = {
  async create(data: {
    court_id: string;
    start_at: string;
    end_at: string;
    notes?: string;
    idempotency_key?: string;
    correlation_id?: string;
    booking_source?: string;
    auto_confirm?: boolean;
    payment_flow_choice?: BookingPaymentFlow;
  }): Promise<CreateBookingV2Response> {
    return callEdgeData<CreateBookingV2Response>(BOOKING_FUNCTION_NAME, "create-v2", { body: data });
  },

  async getCourtDaySchedule(data: {
    court_id: string;
    schedule_date: string;
    duration_minutes?: number;
    correlation_id?: string;
  }): Promise<BookingScheduleResponse> {
    return callEdgeData<BookingScheduleResponse>(BOOKING_FUNCTION_NAME, "schedule", {
      method: "GET",
      params: {
        court_id: data.court_id,
        schedule_date: data.schedule_date,
        ...(typeof data.duration_minutes === "number"
          ? { duration_minutes: String(data.duration_minutes) }
          : {}),
        ...(data.correlation_id ? { correlation_id: data.correlation_id } : {}),
      },
    });
  },

  async previewPrice(data: {
    court_id: string;
    start_at: string;
    end_at: string;
    correlation_id?: string;
  }): Promise<BookingSlotPreview> {
    return callEdgeData<BookingSlotPreview>(BOOKING_FUNCTION_NAME, "preview", { body: data });
  },

  async cancel(bookingId: string, reason?: string): Promise<{ success: boolean }> {
    return callEdgeData<{ success: boolean }>(BOOKING_FUNCTION_NAME, "cancel", {
      body: { booking_id: bookingId, reason },
    });
  },

  async confirm(bookingId: string): Promise<{ success: boolean }> {
    return callEdgeData<{ success: boolean }>(BOOKING_FUNCTION_NAME, "confirm", {
      body: { booking_id: bookingId },
    });
  },

  async createBlock(data: {
    court_id: string;
    starts_at: string;
    ends_at: string;
    title?: string;
    reason?: string;
    block_type?: string;
    metadata?: Record<string, unknown>;
    correlation_id?: string;
  }): Promise<{ block_id: string }> {
    return callEdgeData<{ block_id: string }>(BOOKING_FUNCTION_NAME, "block", { body: data });
  },

  async releaseBlock(data: {
    block_id: string;
    reason?: string;
    correlation_id?: string;
  }): Promise<{ success: boolean }> {
    return callEdgeData<{ success: boolean }>(BOOKING_FUNCTION_NAME, "release-block", { body: data });
  },

  async upsertPricingRule(data: {
    court_id: string;
    rule_id?: string;
    day_of_week?: number | null;
    start_time?: string;
    end_time?: string;
    price_cents: number;
    is_peak?: boolean;
    label?: string;
    priority?: number;
    active?: boolean;
    correlation_id?: string;
  }): Promise<{ rule_id: string }> {
    return callEdgeData<{ rule_id: string }>(BOOKING_FUNCTION_NAME, "upsert-pricing", { body: data });
  },

  async upsertPolicy(data: {
    court_id: string;
    available_payment_flows: BookingPaymentFlow[];
    prebooking_hold_minutes: number;
    correlation_id?: string;
  }): Promise<BookingSchedulePolicy> {
    return callEdgeData<BookingSchedulePolicy>(BOOKING_FUNCTION_NAME, "upsert-policy", { body: data });
  },

  /** Read bookings via direct Supabase query (RLS-protected) */
  async listMine(): Promise<PlayerBookingListItem[]> {
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        id,
        venue_id,
        court_id,
        tenant_id,
        start_at,
        end_at,
        status,
        payment_status,
        total_amount_cents,
        created_at,
        venues:venues!bookings_venue_id_fkey(
          name,
          slug,
          cancellation_rules,
          address,
          city,
          state,
          cover_image_url,
          photos
        ),
        courts:courts!bookings_court_id_fkey(
          name
        )
      `)
      .order("start_at", { ascending: false })
      .limit(50);
    if (error) throw error;
    return (data ?? []) as PlayerBookingListItem[];
  },

  async listByTenant(tenantId: string): Promise<TenantBooking[]> {
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        id,
        tenant_id,
        venue_id,
        court_id,
        created_by,
        start_at,
        end_at,
        status,
        payment_status,
        total_amount_cents,
        notes,
        created_at,
        venues:venues!bookings_venue_id_fkey(name),
        courts:courts!bookings_court_id_fkey(name)
      `)
      .eq("tenant_id", tenantId)
      .order("start_at", { ascending: false })
      .limit(100);
    if (error) throw error;

    const rows = (data ?? []) as TenantBooking[];
    const createdByIds = Array.from(
      new Set(rows.map((booking) => booking.created_by).filter((userId): userId is string => Boolean(userId))),
    );

    if (!createdByIds.length) return rows;

    const { data: profiles, error: profilesError } = await supabase
      .from("profiles")
      .select("user_id, full_name")
      .in("user_id", createdByIds);

    if (profilesError) {
      console.warn("Unable to load booking profile display names", profilesError.message);
      return rows;
    }

    const profilesByUserId = new Map(
      (profiles ?? []).map((profile) => [profile.user_id, { full_name: profile.full_name }]),
    );

    return rows.map((booking) => ({
      ...booking,
      profiles: booking.created_by ? (profilesByUserId.get(booking.created_by) ?? null) : null,
    }));
  },

  async getById(bookingId: string): Promise<BookingDetail> {
    const { data, error } = await supabase
      .from("bookings")
      .select("*, venues(name, slug), courts(name), booking_participants(*, profiles!booking_participants_user_id_fkey(full_name, avatar_url))")
      .eq("id", bookingId)
      .single();
    if (error) throw error;
    return data as BookingDetail;
  },

  async listRecentConflictAttempts(tenantIds: string[]): Promise<ConflictAuditEvent[]> {
    if (!tenantIds.length) return [] as ConflictAuditEvent[];

    const { data, error } = await supabase
      .from("audit_logs")
      .select("id, tenant_id, action, entity_id, metadata, new_value, created_at")
      .in("tenant_id", tenantIds)
      .eq("action", "booking.conflict_attempt")
      .order("created_at", { ascending: false })
      .limit(20);

    if (error) throw error;
    return (data ?? []) as ConflictAuditEvent[];
  },
};
